(ns app.front.view.reset-password.send-link
  (:require [app.front.state-machine :as sm]
            [app.front.util :as futil]
            [app.front.validate :as validate :refer [field-invalid-text field-invalid?]]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(def model {:user/email [[validate/Email :validate/wrong-email-address]]})


(def initial-form-data {::state        nil
                        ::show-errors? false
                        ::errors       {}})

(def state-machine
  {nil                 {::init ::ready}
   ::ready             {::set-form-data ::client-validation
                        ::try-submit    ::submitting}
   ::client-validation {::no-errors    ::ready
                        ::still-errors ::client-validation}
   ::submitting        {::submit-failure ::server-errors
                        ::submit-success ::done}
   ::server-errors     {::set-form-data ::client-validation}})


;; -- Subscriptions -----------------------------------------------------------

(rf/reg-sub
  ::form-data
  (fn [db [_ path]]
    (let [form-data (::form-data db)]
      (if path (get-in form-data path) form-data))))


(rf/reg-sub
  ::state
  :<- [::form-data]
  (fn [form-data _] (get form-data ::state)))


(rf/reg-sub
  ::show-errors?
  :<- [::form-data]
  (fn [form-data _] (get form-data ::show-errors?)))


(rf/reg-sub
  ::server-errors
  :<- [::form-data]
  (fn [form-data _] (get form-data ::server-errors)))


(rf/reg-sub
  ::submit-disabled?
  :<- [::state]
  (fn [state _] (= state ::submitting)))


;; -- Events ------------------------------------------------------------------

(def update-next-state (sm/make-update-next-state state-machine
                                                  [::form-data ::state]))

(def debug
  (rf/after
    (fn [db event]
      (js/console.debug "=======")
      (js/console.debug "state: " (str (-> db ::form-data ::state)))
      (js/console.debug "event: " (str event)))))


(def interceptors [(rf/inject-cofx :tr) (when ^boolean js/goog.DEBUG debug)])


(rf/reg-event-db
  ::init
  interceptors
  (fn [db [event initial-username]]
    (-> db
        (assoc ::form-data (assoc initial-form-data
                                  :user/email initial-username))
        (update-next-state event))))


(rf/reg-event-fx
  ::set-form-data
  interceptors
  (fn [{:keys [db]} [event path value]]
    {:db       (-> db
                   (assoc-in (into [::form-data] path) value)
                   (update-next-state event))
     :dispatch [::validate-fields]}))


(rf/reg-event-fx
  ::email-submitted
  interceptors
  (fn [{:keys [db]} _]
    {:db       (assoc-in db [::form-data ::show-errors?] true)
     :dispatch (if-not (empty? (validate/validate-form (::form-data db) model))
                 [::validate-fields]
                 [::try-submit])}))


(rf/reg-event-fx
  ::validate-fields
  interceptors
  (fn [{:keys [db]} _]
    {:dispatch (if (or (false? (get-in db [::form-data ::show-errors?]))
                       (empty? (validate/validate-form (::form-data db) model)))
                 [::no-errors]
                 [::still-errors])}))


(rf/reg-event-fx
  ::try-submit
  interceptors
  (fn [{:keys [db]} [event _]]
    {:db           (update-next-state db event)
     :comm/command {:command        :user/send-reset-password-link
                    :data           (select-keys (::form-data db) [:user/email])
                    :on-success     [::submit-success]
                    :on-bad-request [::submit-failure]}}))


(rf/reg-event-fx
  ::submit-failure
  interceptors
  (fn [{:keys [db]} [event status body]]
    (js/console.error "::submit-failure" status body)
    {:db (update-next-state db event)}))


(rf/reg-event-db ::no-errors interceptors update-next-state)
(rf/reg-event-db ::still-errors interceptors update-next-state)
(rf/reg-event-db ::submit-success interceptors update-next-state)


;; -- View ------------------------------------------------------------------

(defn enter-email-form [tr]
  (r/with-let [validator #(validate/validate-field % (<sub [::form-data]) model)]
    (let [form-username (<sub [::form-data [:user/email]])]
      [:form.flex.flex-col
       {:on-submit   (fn [e] (.preventDefault e) (>evt [::email-submitted]))
        :no-validate true}
       [:div.cloudpermit--forgot-pass-heading.md:hidden (tr [:forgot-password/heading])]
       [:div.mt-8.leading-tight
        (tr [:forgot-password/body-text])]
       [:div.flex.items-start.mb-6.mt-6
        (cc/text-input {:id            "input-email"
                        :label-text    (tr [:email-address])
                        :auto-focus    true
                        :light         true
                        :type          :email
                        :placeholder   (tr [:forgot-password/placeholder])
                        :class-name    "flex-none bg-white py-6"
                        :default-value form-username
                        :invalid       (field-invalid? (<sub [::show-errors?]) validator :user/email)
                        :invalid-text  (field-invalid-text (<sub [::show-errors?]) validator :user/email tr)
                        :on-change     (fn [e] (>evt [::set-form-data [:user/email] (str/lower-case (-> e .-target .-value))]))})]
       [:div.flex.flex-col.items-start
        (cc/button
          {:type       :submit
           :disabled   (<sub [::submit-disabled?])
           :class-name "justify-center w-full px-8 bg-ui-night-sky-100 hover:bg-ui-night-sky-90 rounded-md text-lg font-bold"}
          (tr [:continue]))]])))


(defn submit-success [tr email]
  [:div.flex.flex-col
   [:div.cloudpermit--forgot-pass-heading (tr [:forgot-password/heading-sent])]
   [:div.my-4.leading-tight
    (tr [:forgot-password/verification] [email])]])


(defn view [username]
  (rf/dispatch-sync [::init username])
  (let [tr (<sub [:tr])]
    (fn []
      [:div.flex.flex-col.items-stretch.max-w-md.w-full
       [:div.flex.flex-col
        (case (<sub [::state])
          ::done [submit-success tr (<sub [::form-data [:user/email]])]
          [enter-email-form tr username])
        [:div.flex.flex-col.pt-4.w-full
         [:div.flex.items-center.p-2.flex-col
          [:a.text-ui-night-sky-100.no-underline.hover:underline
           {:href     (href/href :login)
            :on-click #(futil/set-title (tr [:login/login]))}
           (tr [:back-to-login])]]]]])))
